import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from "react";
import socket from "../../socket";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MdOutlineKeyboardDoubleArrowLeft } from "react-icons/md";
import { MdKeyboardDoubleArrowRight } from "react-icons/md";
import { CiSettings } from "react-icons/ci";
import { IoNotificationsOutline } from "react-icons/io5";
import Profile from "../Home/Profile";
import api from "../../api/axiosConfig";
import { useAuth } from "../Authentication/AuthContext";
import { FaPlus } from "react-icons/fa6";
import { GoPlus } from "react-icons/go";
import "../../styles/layouts/AppLayout.css";
import AddProjectDial from "../DialBox/AddProjectDial";
import AddTimeSheetDial from "../DialBox/AddTimeSheetDial";
import AddUser from "../DialBox/AddUser";
import { ProjectContext } from "../../App";
import { useRolePermissions } from "../Settings/RoleContext";
import TodoDial from "../DialBox/TodoDial";

const AppLayout = () => {
  const { userDetails } = useAuth();
  const { hasPermission } = useRolePermissions();
  const {
    setDialBoxopen,
    setIsNavBarOpen,
    isNavBarOpen,
    openDialog,
    setOpenDialog,
    setAddTimesheetOpen,
    addTimesheetOpen,
    addProjectOpen,
    setAddProjectOpen,
  } = useContext(ProjectContext);
  const [todoDialOpen, setTodoDialOpen] = useState(false);
  const profileRef = useRef(null);
  const [addDialBox, setAddDialBox] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(() => {
    return localStorage.getItem("selectedProject") || "";
  });
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [toDoList, setToDoList] = useState([]);
  const [openTodoCount, setOpenTodoCount] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const userID = userDetails.id;
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const location = useLocation();
  const [activeLink, setActiveLink] = useState(() => {
    return localStorage.getItem("activeLink") || "";
  });
  const [userExistingHoursInMinutes, setUserExistingHoursInMinutes] =
    useState("");
  useEffect(() => {
    const currentPath = location.pathname.split("/")[1];
    setActiveLink(currentPath);
    localStorage.setItem("activeLink", currentPath);
  }, [location]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const playSound = () => {
    const audio = new Audio();
    audio.src = "/sounds/notification.mp3";
    audio.oncanplaythrough = () => {
      audio.play();
    };
    audio.onerror = () => {
      console.error("Audio source is invalid or unsupported.");
    };
  };
  useEffect(() => {
    if (notifications.length > 0) {
      playSound();
    }
  }, [notifications]);

  const fetchProfilePicture = async () => {
    try {
      const response = await api.get(`/api/users/${userID}/profile-picture`, {
        responseType: "blob",
      });

      if (response.status === 200) {
        const imageBlob = response.data;
        const imageUrl = URL.createObjectURL(imageBlob);

        setProfilePicture(imageUrl);
      } else {
        console.error("Failed to fetch profile picture:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching profile picture:", error);
    }
  };

  useEffect(() => {
    fetchProfilePicture();
  }, [userID]);

  const calculateExistingHours = async (user, selectedDate) => {
    try {
      const response = await api.get(
        `/api/timesheets/${user}/${selectedDate.toLocaleDateString("en-CA")}`
      );
      const userTimesheets = response.data;

      let totalMinutes = 0;
      userTimesheets.forEach((timesheet) => {
        const [hours, minutes] = timesheet.duration.split(":").map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          totalMinutes += hours * 60 + minutes;
        }
      });

      let res = (8 * 60 - totalMinutes) / 60;
      res = normalizePlannedHours(res);

      setUserExistingHoursInMinutes(res);
    } catch (error) {
      console.error("Error fetching existing timesheets:", error);
    }
  };
  const updateLastSeen = async () => {
    try {
      await api.post(`/api/users/update-last-seen`, {
        userId: userDetails.id,
        lastSeenAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("❌ Failed to update last seen time:", err);
    }
  };
  useEffect(() => {
    if (!userDetails?.id) return;

    const fetchUnreadMessages = async () => {
      try {
        const response = await api.get(`/api/notifications`, {
          params: {
            userId: userDetails.id,
          },
        });

        const { todos, messages } = response.data;

        const newMessages = messages
          .filter((msg) => msg.sender_id !== userDetails.id)
          .reduce((acc, msg) => {
            // Always keep the latest message per ticket_no
            acc[msg.ticket_no] = msg;
            return acc;
          }, {}); // newMessages is now an object { ticket_no: message }

        const latestMessages = Object.values(newMessages); // Only latest per ticket

        const newNotification = todos.map((newTodo) => ({
          sender: newTodo.created_by_name,
          employeecode: "",
          ticket_no: newTodo.id,
          message: `New To-Do Ticket #${newTodo.id} has been added for you.\nSubject: ${newTodo.subject}\nDescription: ${newTodo.comments}`,
        }));

        setNotifications([...newNotification, ...latestMessages]);
      } catch (error) {
        console.error("❌ Error fetching notifications:", error);
      }
    };

    const interval = setInterval(updateLastSeen, 60000);
    fetchUnreadMessages();

    return () => clearInterval(interval);
  }, [userDetails]);

  useEffect(() => {
    socket.on("project users added", (userIds, projectResult) => {
      const numericUserIds = Array.isArray(userIds)
        ? userIds.map(Number)
        : [Number(userIds)];

      if (numericUserIds.includes(userDetails.id)) {
        setProjects((prevProjects) => {
          const newProjects = projectResult.filter(
            (newProject) =>
              !prevProjects.some((proj) => proj.id === newProject.id)
          );

          return [...prevProjects, ...newProjects];
        });
      }
    });

    socket.on("project users removed", (userIds, projectResult) => {
      const numericUserIds = Array.isArray(userIds)
        ? userIds.map(Number)
        : [Number(userIds)];
      if (numericUserIds.includes(userDetails.id)) {
        setProjects((prevProjects) => {
          return prevProjects.filter(
            (proj) =>
              !projectResult.some((removedProj) => removedProj.id === proj.id)
          );
        });
      }
    });

    socket.on("project deleted", (data) => {
      const idsArray = Array.isArray(data.ids) ? data.ids : [data.ids];
      setProjects((prevProjects) =>
        prevProjects.filter((project) => !idsArray.includes(project.id))
      );
    });
    socket.on("new todo added", async ({ id }) => {
      const ID = Number(id);

      setTimeout(async () => {
        try {
          const res = await api.get(`/api/todolists/todo/${ID}`);
          const newTodo = res.data;

          if (!newTodo) {
            console.error("Error: No new todo found for ID", ID);
            return;
          }

          // Parse user IDs from array or string
          const extractUserIds = (input) => {
            if (Array.isArray(input)) {
              return input
                .map((u) => {
                  // Try both id and name, whatever looks like a number
                  const idCandidate = parseInt(u.id);
                  if (!isNaN(idCandidate)) return idCandidate;
                  const nameCandidate = parseInt(u.name);
                  if (!isNaN(nameCandidate)) return nameCandidate;
                  return null;
                })
                .filter((id) => id !== null);
            } else if (typeof input === "string") {
              const regex = /id:(\d+):/g;
              const matches = [...input.matchAll(regex)];
              return matches.map((m) => parseInt(m[1]));
            }
            return [];
          };

          const assignedIds = extractUserIds(newTodo.assigned_users);
          const ccIds = extractUserIds(newTodo.cc_users);
          const userId = userDetails.id;
          const isUserInvolved =
            assignedIds.includes(userId) ||
            ccIds.includes(userId) ||
            Number(newTodo.forwarded_to_id) === userId;
          if (isUserInvolved || newTodo.created_by !== userID) {
            const newNotification = {
              sender: newTodo.created_by_name,
              employeecode: "", // optionally update this if you have it
              ticket_no: ID,
              message: `New To-Do Ticket #${ID} has been added for you.\nSubject: ${newTodo.subject}\nDescription: ${newTodo.comments}`,
            };

            setNotifications((prev) => [...prev, newNotification]);
            setShowNotification(true);
            playSound();

            setToDoList((prev) => [...prev, newTodo]);
          }
        } catch (error) {
          console.error("Error fetching new todo:", error);
        }
      }, 500);
    });

    socket.on("todo updated", (id, field) => {
      setToDoList((prev) => {
        const updatedList = prev.map((t) =>
          t.id === Number(id) ? { ...t, ...field } : t
        );
        const openCount = updatedList.reduce(
          (count, t) =>
            count + (t.status === "open" || t.status === "reopen" ? 1 : 0),
          0
        );
        setOpenTodoCount(openCount);
        return updatedList;
      });
    });

    const handleNewMessage = (newMessage) => {
      const userId = userDetails.id;

      // Helper to extract user IDs from user objects
      const extractUserIds = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.map((user) => parseInt(user.id)).filter(Boolean);
      };

      // Find the matching To-Do
      const matchedTodo = toDoList.find(
        (todo) => todo.id === newMessage.todolist_id
      );
      if (!matchedTodo) return; // Not relevant
      const assignedIds = extractUserIds(matchedTodo.assigned_users);
      const reportedIds = extractUserIds(matchedTodo.reported_users);
      const ccIds = extractUserIds(matchedTodo.cc_users);

      const isUserInvolved = [
        ...assignedIds,
        ...reportedIds,
        ...ccIds,
        Number(matchedTodo.created_by),
        Number(matchedTodo.forwarded_to_id),
      ].includes(userId);

      if (!isUserInvolved) return;

      const fetchUnreadMessages = async () => {
        try {
          const response = await api.get(`/api/notifications`, {
            params: {
              userId: userDetails.id,
              messageId: newMessage.id,
            },
          });

          const { todos = [], messages = [] } = response.data;

          const newMessages = messages.filter(
            (msg) => msg.sender_id !== userDetails.id
          );

          if (newMessages.length > 0) {
            playSound();

            setNotifications((prev) => {
              const combined = [...prev, ...todos, ...newMessages];

              // Filter only notifications related to user
              const filtered = combined.filter((item) => {
                const todoId = item.todolist_id || item.ticket_no || item.id;
                const todo = toDoList.find((t) => t.id === todoId);
                if (!todo) return false;

                const assigned = extractUserIds(todo.assigned_users);
                const reported = extractUserIds(todo.reported_users);
                const cc = extractUserIds(todo.cc_users);

                return [
                  ...assigned,
                  ...reported,
                  ...cc,
                  Number(todo.created_by),
                  Number(todo.forwarded_to_id),
                ].includes(userId);
              });

              // Deduplicate based on `id`
              const uniqueMap = new Map();
              filtered.forEach((item) => uniqueMap.set(item.id, item));
              return Array.from(uniqueMap.values());
            });

            setShowNotification(true);
          }
        } catch (error) {
          console.error("❌ Error fetching notifications:", error);
        }
      };

      fetchUnreadMessages();
    };

    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("project deleted");
      socket.off("project users added");
      socket.off("project users removed");
      socket.off("todo updated");
      socket.off("new todo added");
      socket.off("new_message");
    };
  }, [toDoList, userDetails?.id]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);

    const filtered = projects.filter(
      (project) =>
        (project?.name?.toLowerCase() || "").includes(
          e.target.value.toLowerCase()
        ) ||
        (project?.projectno?.toLowerCase() || "").includes(
          e.target.value.toLowerCase()
        ) ||
        (project?.displayName?.toLowerCase() || "").includes(
          e.target.value.toLowerCase()
        )
    );

    setFilteredProjects(filtered);
    setShowDropdown(true); // Show the dropdown when the user types
  };

  const handleFocus = () => {
    setSearchTerm(""); // Reset the search term to show all projects
    setFilteredProjects(projects); // Show all projects when focusing on the input
    setShowDropdown(true); // Show the dropdown
  };
  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setDialBoxopen(false);
    setSearchTerm(project.displayName); // Use the concatenated value

    setShowDropdown(false);

    setTimeout(() => {
      localStorage.setItem("selectedProject", project.displayName);
      const frequentlyAccessedProjects =
        JSON.parse(localStorage.getItem("frequentlyAccessedProjects")) || [];
      if (!frequentlyAccessedProjects.includes(project.id)) {
        frequentlyAccessedProjects.push(project.id);
        localStorage.setItem(
          "frequentlyAccessedProjects",
          JSON.stringify(frequentlyAccessedProjects)
        );
      }
      navigate(`/project/${project.id}/dashboard`);
    }, 0);
  };

  // Load selected project from localStorage on component mount
  useEffect(() => {
    const storedProjectId = localStorage.getItem("selectedProject");
    if (storedProjectId) {
      const storedProject = projects.find(
        (project) => project.id === storedProjectId
      );
      setSelectedProject(storedProject);
      setSearchTerm(storedProject?.name || "");
    }

    const frequentlyAccessedProjects =
      JSON.parse(localStorage.getItem("frequentlyAccessedProjects")) || [];
    setProjects((prevProjects) =>
      prevProjects.sort((a, b) => {
        const aFreq = frequentlyAccessedProjects.includes(a.id);
        const bFreq = frequentlyAccessedProjects.includes(b.id);
        return bFreq - aFreq;
      })
    );
  }, [projects]);

  const handleAddBox = () => {
    setAddDialBox((prev) => !prev);
  };

  //close add popup when click outside container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        if (handleAddBox) {
          handleAddBox();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleAddBox]);

  // fetching project details

  const fetchData = async () => {
    try {
      const isAdmin =
        userDetails?.role_name === "Administrator" ||
        userDetails.role_name === "Super Admin";
      const userID = userDetails?.id;

      // Guard clause: only proceed if userID is defined
      if (!userID) {
        console.warn("User ID not available. Skipping fetch.");
        return;
      }

      // Define API endpoints based on role
      const projectEndpoint = isAdmin
        ? "/api/projectlistdata"
        : `/api/projectlistdata/${userID}`;

      const todoEndpoint = isAdmin
        ? "/api/todolists"
        : `/api/todolists/${userID}`;

      // Fetch both endpoints in parallel
      const [projectRes, todoRes] = await Promise.all([
        api.get(projectEndpoint),
        api.get(todoEndpoint),
      ]);
      // Defensive check: Ensure projectRes.data is an array
      const rawProjects = Array.isArray(projectRes.data) ? projectRes.data : [];

      const projectData = rawProjects
        .filter((project) => project.activestatus === 1)
        .map((project) => ({
          ...project,
          displayName: `${project.projectno}-${project.name}`,
        }));

      // Update state
      setToDoList(todoRes.data);
      setOpenTodoCount(
        todoRes.data.reduce(
          (count, t) =>
            count + (t.status === "open" || t.status === "reopen" ? 1 : 0),
          0
        )
      );
      setProjects(projectData);
    } catch (error) {
      console.error("❌ Error fetching data:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // Trigger only when userDetails is fully available
  useEffect(() => {
    if (userDetails?.id) {
      fetchData();
    }
  }, [userDetails]);

  // Filter projects based on search term
  useEffect(() => {
    const filtered = projects.filter((project) =>
      (project?.displayName?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      )
    );
    setFilteredProjects(filtered);
  }, [searchTerm, projects]);

  const resetProjectSelection = (link) => {
    return () => {
      calculateExistingHours(userID, new Date());
      setDialBoxopen(false);
      localStorage.setItem("activeLink", link);
      localStorage.removeItem("openSection");
      localStorage.removeItem("tabIndex");
      localStorage.removeItem("activeproject");
      localStorage.removeItem("selectedUsers");
      localStorage.removeItem("selectedProjects");
      localStorage.removeItem("selectedTask");
      localStorage.removeItem("phases");
      localStorage.removeItem("tasks");
      localStorage.removeItem("selectedPhase");
      localStorage.removeItem("logUserCondition");
      localStorage.removeItem("projectCondition");
      localStorage.removeItem("phaseCondition");
      localStorage.removeItem("taskCondition");
      localStorage.removeItem("billableCondition");
      localStorage.removeItem("approvalStatusCondition");
      localStorage.removeItem("approvalByCondition");
      localStorage.removeItem("selectedAdmin");
      localStorage.removeItem("approvalstatus");
      localStorage.removeItem("billable");
      localStorage.removeItem("categoryValue");
      localStorage.removeItem("categoryCondition");
      localStorage.removeItem("tselectedUsers");
      localStorage.removeItem("tphases");
      localStorage.removeItem("tbillable");
      localStorage.removeItem("tselectedProjects");
      localStorage.removeItem("tlogUserCondition");
      localStorage.removeItem("tprojectCondition");
      localStorage.removeItem("tphaseCondition");
      localStorage.removeItem("tselectedPhase");
      localStorage.removeItem("tbillableCondition");
      localStorage.removeItem("groupBy");
      localStorage.removeItem("expandedTicket");
      localStorage.removeItem("expandedTicketHistory");
      localStorage.removeItem("projectgroupby");
      setSelectedProject(null);
      setSearchTerm("");
    };
  };

  // profile popup open
  const openProfile = useCallback(() => {
    setIsProfileOpen(true);
  }, []);

  // profile popup close
  const closeProfile = useCallback(() => {
    setIsProfileOpen(false);
  }, []);

  // for loading state
  if (loading) {
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <span className="loading-animation">LOADING...</span>
    </div>;
  }

  // Function to get the avatar style based on the first letter of the username
  const getAvatarStyle = () => {
    if (userDetails && userDetails.name) {
      const color = "#6ca0dc";
      return {
        backgroundColor: color,
        color: "white",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "30px",
        height: "32px",
        fontSize: "17px",
        textAlign: "center",
        fontWeight: "800",
        marginTop: "0px",
      };
    }
    return {};
  };

  const firstLetter = userDetails
    ? userDetails.name.charAt(userDetails.name.length - 1).toUpperCase() +
      userDetails.name.charAt(0).toUpperCase()
    : "?";
  function normalizePlannedHours(input) {
    // If input is a decimal (e.g., "1.5"), convert it to HH:MM format
    if (/^[0-9]+([.][0-9]+)?$/.test(input)) {
      const hours = Math.floor(parseFloat(input));
      const minutes = Math.round((parseFloat(input) - hours) * 60);
      return `${hours}:${minutes.toString().padStart(2, "0")}`;
    }

    // If input is in HH:MM format, return it directly
    if (/^[0-9]+:[0-5][0-9]$/.test(input)) {
      return input;
    }

    // If input is a whole number, treat it as hours
    if (/^[0-9]+$/.test(input)) {
      return `${parseInt(input, 10)}:00`;
    }

    // For invalid formats, return an empty string
    return "";
  }

  useEffect(() => {
    if (userID) {
      calculateExistingHours(userID, new Date()); // ✅ Pass the correct date object
    }
  }, [userID, socket]); // ✅ Keeps it dynamic
  const getColorFromId = (id) => {
    if (!id) return "#000";
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 560; // Keeps hue in valid range (0-360)
    const lightness = 35 + (Math.abs(hash) % 25); // Random lightness between 35-60%
    return `hsl(${hue}, 65%, ${lightness}%)`;
  };

  const navigateToTodo = (m) => {
    setNotifications((prev) => prev.filter((n) => n.id !== m.id));
    navigate("/todoList");
    setShowNotification(false);
    localStorage.setItem("expandedTicket", m.ticket_no);
    localStorage.setItem("expandedTicketHistory", true);
  };
  return (
    <>
      {!isNavBarOpen && (
        <span className="arrow-nav-icon slide-icon" title="Show sidebar">
          <MdKeyboardDoubleArrowRight
            style={{ color: "black" }}
            className="icon"
            onClick={() => setIsNavBarOpen(true)}
          />
        </span>
      )}
      {isNavBarOpen && (
        <div className="left-side-navbar">
          <span className="arrow-nav-icon slide-icon" title="Hide sidebar">
            <MdOutlineKeyboardDoubleArrowLeft
              className="icon"
              onClick={() => setIsNavBarOpen(false)}
            />
          </span>
          <img
            className="project-image"
            alt="Projects"
            src="/AppLayout/projects.png"
          />
        </div>
      )}
      <div className="home-layout">
        <div className="left-side-layout-group">
          <div className="tabs-parent">
            <div>
              <Link
                className={`navbar-select-project ${
                  activeLink === "home" ? "active" : ""
                }`}
                to="/home"
                onClick={resetProjectSelection("home")}
              >
                Home
              </Link>
            </div>
            {hasPermission("reports", "create") && (
              <div>
                <Link
                  className={`navbar-select-project ${
                    activeLink === "report" ? "active" : ""
                  }`}
                  to="/report"
                  onClick={resetProjectSelection("report")}
                >
                  Report
                </Link>
              </div>
            )}
            {hasPermission("estimates", "read") && (
              <div>
                <Link
                  className={`navbar-select-project ${
                    activeLink === "estimate" ? "active" : ""
                  }`}
                  to="/estimate"
                  onClick={resetProjectSelection("estimation")}
                >
                  Estimate
                </Link>
              </div>
            )}
            {hasPermission("projects", "read") && (
              <div>
                <Link
                  className={`navbar-select-project ${
                    activeLink === "project" ? "active" : ""
                  }`}
                  to="/project"
                  onClick={resetProjectSelection("project")}
                >
                  Project
                </Link>
              </div>
            )}
            {hasPermission("todolist", "read") && (
              <Link
                className={`navbar-select-project ${
                  activeLink === "todoList" ? "active" : ""
                }`}
                to="/todoList"
                onClick={resetProjectSelection("todoList")}
              >
                To do{" "}
                {openTodoCount !== 0 && (
                  <span className="badge">{openTodoCount}</span>
                )}
              </Link>
            )}
            {hasPermission("timesheets", "read") && (
              <div>
                <Link
                  className={`navbar-select-project ${
                    activeLink === "timesheet" ? "active" : ""
                  }`}
                  to="/timesheet/overview"
                  onClick={resetProjectSelection("overview")}
                >
                  Overview
                </Link>
              </div>
            )}
            {hasPermission("projects", "read") && (
              <div className="select-project-parent" ref={dropdownRef}>
                <input
                  type="text"
                  placeholder="Search or Select Projects"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleFocus}
                  onClick={() =>
                    localStorage.setItem("activePLink", "dashboard")
                  }
                  onMouseDown={() => handleClickOutside}
                  className={`project-search-input ${
                    activeLink === "project" ? "active" : ""
                  }`}
                />
                {showDropdown && (
                  <ul className="project-dropdown">
                    {filteredProjects.map((project) => (
                      <li
                        key={project.id}
                        onClick={() => handleProjectSelect(project)}
                        onMouseDown={() => handleClickOutside}
                        className="project-dropdown-item"
                      >
                        {project.displayName}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="right-side-parent">
          <GoPlus
            onClick={handleAddBox}
            className="plus-icon"
            title="Quick Actions"
          />

          <>
            <Link
              to={
                hasPermission("settings", "update")
                  ? "settings"
                  : "settings/user-details"
              }
            >
              {" "}
              <CiSettings
                onClick={resetProjectSelection("settings")}
                className={`settings-icon ${
                  activeLink === "settings" ? "active" : ""
                }`}
                title="Configure Settings"
              />
            </Link>
            <IoNotificationsOutline
              onClick={() => {
                resetProjectSelection("notification");
                setShowNotification((prev) => !prev);
              }}
              className={`notification-icon ${
                activeLink === "notification" ? "active" : ""
              }`}
              title="Notifications"
            />
            <span className="notification-count">
              {notifications.length === 0 ? "" : notifications.length}
            </span>
            <p className="existing-hrs">
              {userExistingHoursInMinutes === "0:00"
                ? ""
                : ` ${userExistingHoursInMinutes}`}
            </p>
          </>

          <a
            style={{ cursor: "pointer", backgroundColor: "transparent" }}
            alt="profile"
            title="profile"
            onClick={openProfile}
          >
            {profilePicture && profilePicture !== "null" ? (
              <img
                className="avatar-icon2"
                style={getAvatarStyle()}
                src={profilePicture}
                alt="profile"
              />
            ) : (
              <span className="avatar-icon2" style={getAvatarStyle()}>
                {firstLetter}
              </span>
            )}
          </a>
        </div>
        <hr className="horizontal-line" />
      </div>
      {showNotification && (
        <div className="timesheet-overlay-add-time-log">
          <div className="notification-con">
            <ul className="notification-ul">
              {notifications.map((m) => (
                <li
                  className="notification-li"
                  onClick={() => navigateToTodo(m)}
                >
                  <div style={{ display: "flex" }}>
                    <p
                      style={{
                        fontSize: "13px",
                        color: getColorFromId(m.employeecode),
                        fontWeight: "bold",
                      }}
                    >
                      {m.sender}-{m.employeecode} :
                    </p>
                    <p className="ticket-no">#TicketNo:{m.ticket_no}</p>
                  </div>
                  <p
                    style={{
                      whiteSpace: "pre-line",
                      fontSize: "11.5px",
                      marginLeft: "16px",
                    }}
                  >
                    {m.message}
                  </p>
                </li>
              ))}
            </ul>
            {notifications.length > 0 && (
              <button
                onClick={() => {
                  setNotifications([]);
                  updateLastSeen();
                  setShowNotification(false);
                }}
                className="notification-btn"
              >
                {notifications.length === 1 ? "Clear" : "Clear All"}
              </button>
            )}
          </div>
        </div>
      )}
      {isProfileOpen && <Profile onClose={closeProfile} />}
      {addDialBox && (
        <div className="add-dial-box-container" ref={profileRef}>
          <div className="add-container"></div>

          <>
            {hasPermission("users", "create") && (
              <div className="add-container" style={{ cursor: "pointer" }}>
                <FaPlus className="add-icon-plus" />
                <button
                  className="add-user-layout"
                  style={{ cursor: "pointer" }}
                  onClick={() => setOpenDialog((prev) => !prev)}
                >
                  Add User
                </button>
              </div>
            )}
            {hasPermission("projects", "create") && (
              <div className="add-container" style={{ cursor: "pointer" }}>
                <FaPlus className="add-icon-plus" />
                <button
                  className="add-user-layout"
                  style={{ cursor: "pointer" }}
                  onClick={() => setAddProjectOpen(true)}
                >
                  Add Project
                </button>
              </div>
            )}
            {hasPermission("timesheets", "create") && (
              <div className="add-container" style={{ cursor: "pointer" }}>
                <FaPlus className="add-icon-plus" />
                <button
                  className="add-user-layout"
                  style={{ cursor: "pointer" }}
                  onClick={() => setAddTimesheetOpen(true)}
                >
                  Add Timesheet
                </button>
              </div>
            )}
            {hasPermission("todolist", "create") && (
              <div className="add-container" style={{ cursor: "pointer" }}>
                <FaPlus className="add-icon-plus" />
                <button
                  className="add-user-layout"
                  style={{ cursor: "pointer" }}
                  onClick={() => setTodoDialOpen(true)}
                >
                  Add To Do
                </button>
              </div>
            )}
          </>
        </div>
      )}
      {openDialog && <AddUser />}
      {addTimesheetOpen && <AddTimeSheetDial />}
      {addProjectOpen && <AddProjectDial />}
      {todoDialOpen && <TodoDial setTodoDialOpen={setTodoDialOpen} />}
    </>
  );
};

export default AppLayout;
